import { Component, AfterViewInit, inject, signal, WritableSignal, computed, Inject, PLATFORM_ID, ChangeDetectorRef, viewChild, Signal, OnInit, OnDestroy } from '@angular/core';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatStep, MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { AddressService, NavegoAddressResponse } from '../../shared/address.service';
import { PersonalInformation, SignUpProducts } from './signup.types';
import { CommonModule, isPlatformBrowser, Location } from '@angular/common';
import { AccountService } from '../../shared/account.service';
import { Router, RouterLink } from '@angular/router';
import { LocalStorageService } from '../../shared/local-storage.service';
import { SignalsStoreService } from '../../shared/signals-store.service';
import { Session } from '../../shared/types/session.type';
import { LOCALSTORAGE_KEYS } from '../../shared/constants/databases';
import { ArrayOptionsItem, NewAccountPayload, AccountSettings } from '../../shared/types/account.types';
import { PaymentMethodService } from '../../settings/billing/payment-method/payment-method.service';
import { MatRadioModule } from '@angular/material/radio';
import { PersonalInformationSignupComponent } from './personal-information-signup/personal-information-signup.component';
import { CheckAddressSignupComponent } from './check-address-signup/check-address-signup.component';
import { PaymentMethodSignupComponent } from './payment-method-signup/payment-method-signup.component';
import { ProfileService } from '../../settings/account/profile/profile.service';
import { finalize, tap } from 'rxjs';
import { ModalContentService } from '../../shared/modal-content/modal-content.service';
import { ModalContentTypes } from '../../shared/constants/modal-content-types';
import { ApiResponse } from '../../shared/common/types';
import { HTTP_STATUS_CODES } from '../../shared/constants/errors';
import { FooterComponent } from '../../shared/footer/footer.component';
import { FaqsComponent } from './faqs/faqs.component';
import { SubscriptionsSignupComponent } from './subscriptions-signup/subscriptions-signup.component';
import { ProductsService } from '../../product/products.service';
import { Product } from '../../product/product.types';
import { HeaderService } from '../../shared/header/header.service';
import { DeliveriesService } from '../../settings/account/deliveries/deliveries.service';
import { scrollToTop } from '../../shared/common/utils';
import { MarketingReferral } from '../../shared/types/common.types';
import { checkSignupCouponCodes } from '../../shared/common/singup.utils';
import { PromotionsService } from '../../shared/promotions.service';
import { NotificationService } from '../../shared/notification/notification.service';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-signup',
  providers: [
    provideNativeDateAdapter(),
    PaymentMethodService,
    ProfileService
  ],
  imports: [
    CommonModule,
    MatButtonModule,
    MatStepperModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatAutocompleteModule,
    MatProgressBarModule,
    RouterLink,
    MatRadioModule,
    CheckAddressSignupComponent,
    PersonalInformationSignupComponent,
    PaymentMethodSignupComponent,
    FooterComponent,
    FaqsComponent,
    SubscriptionsSignupComponent,
    NgbTooltip
  ],
  templateUrl: './signup.component.html',
  styleUrl: './signup.component.scss'
})

export class SignupComponent implements AfterViewInit, OnInit, OnDestroy {

  //#region Services

  private signalsStoreService = inject(SignalsStoreService);

  private addressService = inject(AddressService);

  localStorageService = inject(LocalStorageService);

  accountService = inject(AccountService);

  cdr = inject(ChangeDetectorRef);

  #router = inject(Router);

  #location = inject(Location);

  #modalContentService = inject(ModalContentService);

  #productsService = inject(ProductsService);

  #headerService = inject(HeaderService);

  #deliveriesService = inject(DeliveriesService);


  #promotionsService = inject(PromotionsService);

  #notificationService = inject(NotificationService);

  //#endregion

  //#region Properties

  matStepper = viewChild.required<MatStepper>(MatStepper);

  personalInformationComponent: Signal<PersonalInformationSignupComponent> = viewChild.required('personalInformationComponent');

  firstStep = viewChild.required<MatStep>('firstStep');

  paymentMethodStep = viewChild.required<MatStep>('paymentMethodStep');

  isLimitedUser = computed(() => this.signalsStoreService.isLimitedUser());

  formValidityPersonalInformation: boolean | undefined;

  formValidityShoppingPreferences: boolean | undefined;

  formValidityPaymentMethod: boolean | undefined;

  deliveryAddressInformation: any;

  deliverySelectedDay: any;

  deliverySelectedTimeWindow: any;

  deliveryOptionSelected: any;

  personalInformationData: any;

  shopforValue: any;

  dietaryRestrictionsValues: any;

  paymentMethodStepControl: FormControl = new FormControl(true);

  personalInformation: WritableSignal<PersonalInformation | null> = signal(null);

  skipPaymentMethodStep: boolean = false;

  isLoadingContent: any = this.addressService.isLoading;

  isWaitingResponse: any = signal(false);

  shouldDisableCreateClientButton = signal(false);

  shouldSetUpSettings: boolean = true;

  subscriptionCard: WritableSignal<any[]> = signal([]);

  logoVersionNumber = computed(() => this.signalsStoreService.logoVersionNumber());

  hasGlobalMessages = computed(() => !!this.signalsStoreService.globalMessages().length);

  signupProducts = signal<Map<number | string, SignUpProducts>>(new Map());

  currentStepIndex = signal(0)

  signupPromotionInfo = computed(() => this.signalsStoreService.signupPromotionInfo());

  isLoadedMarketingValidations = computed(() => this.signalsStoreService.isLoadedMarketingValidation());

  isTryingToApplyCoupon = signal(false);

  signupPromotionInfoFromLS = computed(() => this.signalsStoreService.signupPromotionInfoLS());

  //#endregion

  //#region Constructor

  constructor(@Inject(PLATFORM_ID) private platformId: any) { }

  ngOnInit(): void {
    this.signalsStoreService.isInSignupComponent.set(true);
  }

  ngOnDestroy(): void {
    this.signalsStoreService.isInSignupComponent.set(false);
  }

  ngAfterViewInit() {
    if (!isPlatformBrowser(this.platformId)) return;

    const signupSelectedMembership: any = this.localStorageService.get(LOCALSTORAGE_KEYS.SIGNUP_SELECTED_MEMBERSHIP);
    if (signupSelectedMembership?.id)
      this.deliveryOptionSelected = signupSelectedMembership;

    const sessionStored: Session = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION) as any
    const hasPaymentMethod = !!(sessionStored)?.settings?.hasPaymentMethod;

    if (this.signalsStoreService.hasSession() && hasPaymentMethod) {
      this.#location.replaceState('/shop');
      window.location.reload();
      return;
    }

    const stepToLoad = this.signalsStoreService.hasSession() && !hasPaymentMethod ? 2 : 0;
    // Load first step or payment method step if hasSession
    if (stepToLoad > 0) {
      for (let index = 0; index < stepToLoad; index++) {
        this.matStepper().steps.toArray()[index].completed = true;
      }

      this.matStepper().selectedIndex = stepToLoad;
    }

    this.cdr.detectChanges();
  }

  //#endregion

  //#region Methods

  continue() {

    this.firstStep().completed = true;

    if (this.matStepper().selectedIndex === 2) {
      this.paymentMethodStep().completed = true;
      this.#setUpStepSubscriptions();
    }

    this.matStepper().next();

    const { selectedIndex } = this.matStepper();

    if (selectedIndex === 1) {
      this.firstStep().editable = false;
      if (this.shouldSetUpSettings) {
        this.getAccountSettings();
        this.shouldSetUpSettings = false;
      }
    }

    scrollToTop('scroll-to-top-ref');
  }

  createNewClient() {
    if (this.shouldDisableCreateClientButton()) return;
    this.shouldDisableCreateClientButton.set(true);
    const payload = this.setUpNewClientPayload();
    if (!payload) return;

    this.accountService
      .create(payload)
      .pipe(
        finalize(() => this.shouldDisableCreateClientButton.set(false))
      )
      .subscribe({
        next: (res: ApiResponse<Session>) => {
          const { data, status } = res;
          if (status === HTTP_STATUS_CODES.CONFLICT)
            return this.#showModalEmailAlreadyInUse(payload.accountInfo.email);
          if (data?.isTimeSlotCapacityFull) {
            this.firstStep().editable = true;
            this.matStepper().previous();
          } else if (data) {
            if (this.deliveryOptionSelected.configuration.requirePaymentMethod)
              this.accountService.setMustFetchStates();

            this.matStepper().next();
            scrollToTop('scroll-to-top-ref');
            this.shouldDisableCreateClientButton.set(false);
          }
        }
      });
  }

  #showModalEmailAlreadyInUse(email: string) {
    this.#modalContentService.openModal(ModalContentTypes.CONFIRMATION, {
      title: 'Email already in use',
      textContent: `Hey! Looks like you already have an account with us. Would you like to login with <b><i>${email}</i></b>?`,
      cancelButtonText: 'Try another email',
      confirmButtonText: 'Click here to login',
    }).closed
      .subscribe((res: { confirm: boolean } | null) => {
        if (!res?.confirm) return this.personalInformationComponent().clearEmailField();
        this.localStorageService.set(LOCALSTORAGE_KEYS.PRELOADED_EMAIL, email);
        this.#router.navigate(['/login']);
      })
  }

  private getAccountSettings() {
    this.accountService.getAccountSettings().subscribe({
      next: (accountSettings: AccountSettings | null) => {
        if (accountSettings) {
          this.setUpAccountSettings(accountSettings);
        }
      }
    });
  }

  private setUpAccountSettings(accountSettings: AccountSettings) {
    this.personalInformation.set({
      contactPerson: {
        email: null,
        firstName: null,
        lastName: null,
        phone: null,
      },
      dietaryRestrictions: accountSettings.dietaryPreferences?.map((preference: ArrayOptionsItem) => {
        return { ...preference, completed: false };
      }) || [],
      peopleShoppingFor: accountSettings.peopleShoppingFor || [],
      email: '',
      firstName: '',
      lastName: '',
      password: '',
      phone: '',
      referralSource: '',
      advertisement: '',
      shopfor: null,
      receiveNewsletter: true,
      receiveText: true
    });
  }

  private setUpNewClientPayload(): NewAccountPayload | null {
    try {
      const { firstName, lastName, phone, email, password, birthday, advertisement, dietaryRestrictions = this.dietaryRestrictionsValues, contactPerson: contact, receiveNewsletter, receiveText, receiveTextDelivery } = this.personalInformationData() || {};
      const { additionalAddress: additional, zipCode: zip, deliveryNotes: noteDriver } = this.deliveryAddressInformation()
      const { street, zoneId, zoneName, city, state: stateCode, latitude, longitude } = this.addressService.navegoAddress() as NavegoAddressResponse || {}
      const res = {
        accountInfo: {
          dietaryPreferences: dietaryRestrictions?.filter((value: any) => value.completed).map((value: any) => { return { id: value.id, name: value.name } }) || null,
          email,
          firstName,
          lastName,
          noteDriver,
          password,
          phone,
          birthday
        },
        settings: {
          receiveNewsletter,
          receiveText,
          receiveTextDelivery,
          delivery: {
            type: this.deliveryOptionSelected.id,
            dayId: this.deliverySelectedDay.id,
            timeWindowId: this.deliverySelectedTimeWindow === -1 ? null : this.deliverySelectedTimeWindow
          },
          peopleShoppingForId: this.shopforValue
        },
        contact
      } as NewAccountPayload

      if (this.deliveryOptionSelected.configuration.requireAddress) {
        res.address = {
          additional: additional,
          city: city,
          latitude: latitude,
          longitude: longitude,
          stateCode: stateCode,
          street: street,
          zip: zip,
          zoneId: zoneId || this.deliverySelectedDay.zoneId
        }
      } else {
        res.address = {
          zoneId: this.deliverySelectedDay.zoneId
        }
      }

      return res;
    } catch (error) {
      console.log('error >>>', error);
      return null;
    }
  }

  #setUpStepSubscriptions() {
    this.#deliveriesService.getDeliveryDates();
    this.#getSignUpProducts();
    this.#headerService.getFrequencies();
    this.#deliveriesService.getDeliveryZoneInfo().subscribe();
  }

  #getSignUpProducts() {

    this.signupProducts.set(new Map());

    this.#productsService
      .getSignupProducts()
      .pipe(
        tap(response => this.#setUpSignupProducts(response.data))
      )
      .subscribe();
  }

  #setUpSignupProducts(products: Product[]) {

    products = products.filter(x => x.isSubscription);

    if (!products.length)
      return;

    const resp = new Map<number | string, SignUpProducts>();
    for (const product of products) {

      const { id, name } = product.category;

      if (!resp.has(product.category.id)) {
        resp.set(id, { category: name, products: [product] });
        continue;
      }

      const data = resp.get(id);
      if (data) {
        data.products.push(product);
        resp.set(id, data);
      }
    }

    this.signupProducts.set(resp);
  }

  goBack() {
    this.#location.back();
  }


  applySignupCoupon(code: string) {

    if (!code) return this.#notificationService.show({ text: 'You should provide a coupon code.', type: 'error' });

    this.isTryingToApplyCoupon.set(true);

    const marketingReferral: MarketingReferral = {
      utmCampaign: code
    };

    checkSignupCouponCodes({
      marketingReferral,
      signalsStoreService: this.signalsStoreService,
      localStorageService: this.localStorageService,
      promotionsService: this.#promotionsService,
      showDefaultMessage: true
    }).pipe(
      tap(res => {
        if (res.name) {
          const currentPath = this.#location.path().split('?')[0];
          this.#location.replaceState(currentPath);
        }
      })
    ).subscribe();

  }

  clearSignupPromotionInfo() {
    this.signalsStoreService.setSignupPromotionInfo(null);
  }

  restoreSignupInfo() {
    const signupPromotionInfo = this.localStorageService.get(LOCALSTORAGE_KEYS.PROMOTION_INFO);
    if (!signupPromotionInfo) return;
    this.signalsStoreService.setSignupPromotionInfo(signupPromotionInfo);
  }

  //#endregion
}
